import { useEffect, useRef, useState } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { BilingualMarkdownEditor } from '../../components/MarkdownField'

// Reuses Tournament-API's /course-website/extract-styles endpoint —
// it's course-independent (just fetches a URL + calls Anthropic) so
// resellers can use it without us adding a parallel endpoint.

/**
 * Brand profile editor — Phase 2.
 *
 * Form for the account-level default brand. Per-package overrides are
 * added in Phase 6 (a different UI in the package builder).
 */
export default function BrandProfile() {
  const { t } = useLanguage()
  const [form, setForm] = useState(initial())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState(null)
  const fileRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  // AI brand extract state
  const [extractUrl, setExtractUrl] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [extractResult, setExtractResult] = useState(null)
  const [extractError, setExtractError] = useState(null)

  useEffect(() => {
    tournamentApi.get('/portal/reseller/brand')
      .then(r => {
        if (r.data?.brand) setForm(fromServer(r.data.brand))
        setLoading(false)
      })
      .catch(err => { setError(err?.response?.data?.message || err.message); setLoading(false) })
  }, [])

  function setField(k) { return (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e })) }
  function setNested(group, k) { return (e) => setForm(f => ({ ...f, [group]: { ...f[group], [k]: e?.target ? e.target.value : e } })) }

  async function handleSave() {
    setSaving(true); setError(null)
    try {
      const { data } = await tournamentApi.put('/portal/reseller/brand', toServer(form))
      if (data?.brand) setForm(fromServer(data.brand))
      setSavedAt(new Date())
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally { setSaving(false) }
  }

  async function handleExtract() {
    const url = extractUrl.trim() || form.website?.trim()
    if (!url) {
      setExtractError(t('sp_extract_enter_url_first', 'Enter your website URL above (or in the Website field below) first.'))
      return
    }
    setExtracting(true); setExtractError(null); setExtractResult(null)
    try {
      const { data } = await tournamentApi.post('/course-website/extract-styles', { url })
      setExtractResult(data)
      if (data.source === 'failed') {
        setExtractError(data.message || t('sp_extract_could_not', 'Could not extract from that URL.'))
        return
      }
      // Apply extracted values to the brand form. We pull the same fields
      // that the Stay & Play extractor surfaces; the reseller brand profile
      // only has color fields (no font fields), so we ignore headerFont/bodyFont.
      setForm(f => ({
        ...f,
        primaryColor:   data.primaryColor   || f.primaryColor,
        secondaryColor: data.secondaryColor || data.accentColor || f.secondaryColor,
        // If website wasn't set yet, persist what they typed
        website: f.website || (url.startsWith('http') ? url : `https://${url}`),
      }))
    } catch (err) {
      setExtractError(err?.response?.data?.message || err.message || 'Extraction failed')
    } finally { setExtracting(false) }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true); setError(null)
    try {
      const fd = new FormData()
      fd.append('logo', file)
      const { data } = await tournamentApi.post('/portal/reseller/brand/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setForm(f => ({ ...f, logoUrl: data.logoUrl }))
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  if (loading) return <ResellerShell title={t('sp_brand_profile_title', 'Brand profile')}><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>

  return (
    <ResellerShell title={t('sp_brand_profile_title', 'Brand profile')}>
      <p style={{ color: '#64748b', maxWidth: 720, marginTop: 0, marginBottom: 24, fontSize: 14 }}>
        {t('sp_brand_intro', 'Customer-facing emails and iframes use these details. Set them once at the account level; you can override per-package in Phase 6 if you want a special package landing somewhere.')}
      </p>

      {error && <div style={errorBox}>{error}</div>}
      {savedAt && <div style={{ ...successBox, marginBottom: 16 }}>{t('sp_saved_at_time', 'Saved at {time}.', { time: savedAt.toLocaleTimeString() })}</div>}

      <Section title={t('sp_match_my_website_ai', 'Match my website (AI-powered)')} subtitle={t('sp_section_match_website_sub', "Paste your website URL and we'll extract your brand colors automatically. You can fine-tune below.")}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="url"
            placeholder="https://yourbrand.com"
            value={extractUrl}
            onChange={e => setExtractUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleExtract() } }}
            style={{ ...inp, flex: 1, minWidth: 240 }}
          />
          <button onClick={handleExtract} disabled={extracting} style={{ ...primaryBtn, opacity: extracting ? 0.6 : 1 }}>
            {extracting ? t('sp_analyzing_ellipsis', 'Analyzing…') : t('sp_btn_extract_brand', '✨ Extract brand')}
          </button>
        </div>
        {extractError && <div style={{ ...errorBox, marginTop: 12, marginBottom: 0 }}>{extractError}</div>}
        {extractResult && extractResult.source !== 'failed' && (
          <div style={{ marginTop: 12, padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, fontSize: 13, color: '#065f46' }}>
            {t('sp_extract_pulled_prefix', '✓ Pulled from')} <code>{extractResult.url}</code>. {t('sp_extract_pulled_suffix', 'Colors below were updated — click')} <strong>{t('sp_btn_save_brand_profile', 'Save brand profile')}</strong> {t('sp_extract_keep_them', 'at the bottom to keep them.')}
            {(extractResult.primaryColor || extractResult.secondaryColor || extractResult.accentColor) && (
              <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                {extractResult.primaryColor && <span><span style={{ display: 'inline-block', width: 14, height: 14, background: extractResult.primaryColor, borderRadius: 3, verticalAlign: 'middle', marginRight: 4, border: '1px solid #00000020' }} />{t('sp_color_primary_label', 'primary {color}', { color: extractResult.primaryColor })}</span>}
                {extractResult.secondaryColor && <span><span style={{ display: 'inline-block', width: 14, height: 14, background: extractResult.secondaryColor, borderRadius: 3, verticalAlign: 'middle', marginRight: 4, border: '1px solid #00000020' }} />{t('sp_color_secondary_label', 'secondary {color}', { color: extractResult.secondaryColor })}</span>}
                {extractResult.accentColor && <span><span style={{ display: 'inline-block', width: 14, height: 14, background: extractResult.accentColor, borderRadius: 3, verticalAlign: 'middle', marginRight: 4, border: '1px solid #00000020' }} />{t('sp_color_accent_label', 'accent {color}', { color: extractResult.accentColor })}</span>}
              </div>
            )}
          </div>
        )}
      </Section>

      <Section title={t('sp_logo', 'Logo')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ width: 120, height: 120, background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {form.logoUrl
              ? <img src={form.logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('sp_no_logo', 'No logo')}</span>}
          </div>
          <div>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.svg" onChange={handleLogoUpload} style={{ display: 'none' }} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} style={primaryBtn}>
              {uploading ? t('sp_uploading', 'Uploading…') : (form.logoUrl ? t('sp_btn_replace_logo', 'Replace logo') : t('sp_btn_upload_logo', 'Upload logo'))}
            </button>
            {form.logoUrl && (
              <button onClick={() => setForm(f => ({ ...f, logoUrl: null }))} style={{ ...secondaryBtn, marginLeft: 8 }}>{t('sp_remove', 'Remove')}</button>
            )}
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{t('sp_logo_formats_hint', 'JPG/PNG/WebP/SVG, max 5MB.')}</div>
          </div>
        </div>
      </Section>

      <Section title={t('sp_section_theme_colors', 'Theme colors')}>
        <div style={row}>
          <ColorField label={t('sp_primary_label', 'Primary')} value={form.primaryColor} onChange={setField('primaryColor')} />
          <ColorField label={t('sp_secondary_label', 'Secondary')} value={form.secondaryColor} onChange={setField('secondaryColor')} />
        </div>
      </Section>

      <Section title={t('sp_section_how_it_works', 'How it works')} subtitle={t('sp_section_how_it_works_sub', 'Optional. Adds a “How it works?” link under the Continue button on your customer embed; clicking it expands the text you write here. Write both languages — the embed shows whichever matches the customer’s language.')}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#0f172a', cursor: 'pointer', marginBottom: form.howItWorksEnabled ? 14 : 0 }}>
          <input type="checkbox" checked={!!form.howItWorksEnabled} onChange={e => setForm(f => ({ ...f, howItWorksEnabled: e.target.checked }))} />
          {t('sp_show_hiw_on_embeds', 'Show “How it works?” on my embeds')}
        </label>
        {form.howItWorksEnabled && (
          <BilingualMarkdownEditor
            valueEn={form.howItWorksEn} valueFr={form.howItWorksFr}
            onChangeEn={v => setForm(f => ({ ...f, howItWorksEn: v }))}
            onChangeFr={v => setForm(f => ({ ...f, howItWorksFr: v }))}
          />
        )}
      </Section>

      <Section title={t('sp_section_company', 'Company')}>
        <Field label={t('sp_field_legal_name', 'Legal name')}><input style={inp} value={form.legalName || ''} onChange={setField('legalName')} placeholder={t('sp_company_inc_placeholder', 'Your Company Inc.')} /></Field>
        <div style={row}>
          <Field label={t('sp_phone', 'Phone')} style={{ flex: 1 }}><input style={inp} value={form.phone || ''} onChange={setField('phone')} placeholder="+1 902 555 1212" /></Field>
          <Field label={t('sp_field_support_email', 'Support email')} style={{ flex: 1 }}><input type="email" style={inp} value={form.supportEmail || ''} onChange={setField('supportEmail')} placeholder="support@yourbrand.com" /></Field>
        </div>
        <Field label={t('website', 'Website')}><input type="url" style={inp} value={form.website || ''} onChange={setField('website')} placeholder="https://yourbrand.com" /></Field>
      </Section>

      <Section title={t('sp_section_tax_rates', 'Tax rates')} subtitle={t('sp_section_tax_rates_sub', 'Applied to customer bookings. Pick a preset for your jurisdiction or build a custom stack.')}>
        <TaxRates />
      </Section>

      <Section title={t('sp_section_mailing_address', 'Mailing address')}>
        <AddressFields value={form.mailingAddress} onChange={(k) => setNested('mailingAddress', k)} />
      </Section>

      <Section title={t('sp_section_business_address', 'Business address')} subtitle={t('sp_section_business_address_sub', 'Defaults to mailing address if blank.')}>
        <AddressFields value={form.businessAddress} onChange={(k) => setNested('businessAddress', k)} />
      </Section>

      <Section title={t('sp_section_social_handles', 'Social handles')} subtitle={t('sp_section_social_handles_sub', 'Shown as icons in email footers.')}>
        <div style={row}>
          <Field label={t('sp_field_facebook', 'Facebook')}  style={{ flex: 1 }}><input style={inp} value={form.social?.facebook  || ''} onChange={setNested('social', 'facebook')}  placeholder="yourbrand" /></Field>
          <Field label={t('sp_field_instagram', 'Instagram')} style={{ flex: 1 }}><input style={inp} value={form.social?.instagram || ''} onChange={setNested('social', 'instagram')} placeholder="yourbrand" /></Field>
        </div>
        <div style={row}>
          <Field label={t('sp_field_twitter_x', 'Twitter / X')} style={{ flex: 1 }}><input style={inp} value={form.social?.twitter  || ''} onChange={setNested('social', 'twitter')}  placeholder="yourbrand" /></Field>
          <Field label={t('sp_field_tiktok', 'TikTok')}      style={{ flex: 1 }}><input style={inp} value={form.social?.tiktok   || ''} onChange={setNested('social', 'tiktok')}   placeholder="yourbrand" /></Field>
          <Field label={t('sp_field_linkedin', 'LinkedIn')}    style={{ flex: 1 }}><input style={inp} value={form.social?.linkedin || ''} onChange={setNested('social', 'linkedin')} placeholder="your-brand" /></Field>
        </div>
      </Section>

      <Section title={t('sp_section_internal_notes', 'Internal notes')} subtitle={t('sp_section_internal_notes_sub', 'Only visible to your team and InteGolf staff.')}>
        <textarea style={{ ...inp, minHeight: 80 }} value={form.notes || ''} onChange={setField('notes')} />
      </Section>

      <div style={{ position: 'sticky', bottom: 0, padding: '20px 0', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={handleSave} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1, padding: '12px 24px' }}>
          {saving ? t('sp_saving', 'Saving…') : t('sp_btn_save_brand_profile', 'Save brand profile')}
        </button>
      </div>
    </ResellerShell>
  )
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#0f172a' }}>{title}</h3>
      {subtitle && <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748b' }}>{subtitle}</p>}
      <div style={{ marginTop: subtitle ? 4 : 12 }}>{children}</div>
    </div>
  )
}

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}

function ColorField({ label, value, onChange }) {
  return (
    <Field label={label} style={{ flex: 1 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="color" value={value || '#000000'} onChange={onChange} style={{ width: 44, height: 36, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6 }} />
        <input type="text" value={value || ''} onChange={onChange} style={{ ...inp, flex: 1 }} placeholder="#00d19f" />
      </div>
    </Field>
  )
}


function AddressFields({ value, onChange }) {
  const { t } = useLanguage()
  const v = value || {}
  return (
    <>
      <Field label={t('sp_field_address_line1', 'Address line 1')}><input style={inp} value={v.line1 || ''} onChange={onChange('line1')} /></Field>
      <Field label={t('sp_field_address_line2', 'Address line 2')}><input style={inp} value={v.line2 || ''} onChange={onChange('line2')} /></Field>
      <div style={row}>
        <Field label={t('city', 'City')}        style={{ flex: 1 }}><input style={inp} value={v.city || ''} onChange={onChange('city')} /></Field>
        <Field label={t('sp_field_region_prov', 'Region/Prov')} style={{ flex: 1 }}><input style={inp} value={v.region || ''} onChange={onChange('region')} /></Field>
        <Field label={t('sp_field_postal_code', 'Postal code')} style={{ flex: 1 }}><input style={inp} value={v.postalCode || ''} onChange={onChange('postalCode')} /></Field>
      </div>
      <Field label={t('country', 'Country')}><input style={inp} value={v.country || ''} onChange={onChange('country')} placeholder="CA" /></Field>
    </>
  )
}

function initial() {
  return { logoUrl: null, primaryColor: '', secondaryColor: '', legalName: '', phone: '', supportEmail: '', website: '', mailingAddress: {}, businessAddress: {}, social: {}, notes: '', howItWorksEnabled: false, howItWorksEn: '', howItWorksFr: '' }
}
function fromServer(b) {
  return {
    logoUrl: b.LogoUrl,
    primaryColor: b.PrimaryColor || '',
    secondaryColor: b.SecondaryColor || '',
    legalName: b.LegalName || '',
    phone: b.Phone || '',
    supportEmail: b.SupportEmail || '',
    website: b.Website || '',
    mailingAddress: b.MailingAddress || {},
    businessAddress: b.BusinessAddress || {},
    social: b.Social || {},
    notes: b.Notes || '',
    howItWorksEnabled: !!b.HowItWorksEnabled,
    howItWorksEn: b.HowItWorksEn || '',
    howItWorksFr: b.HowItWorksFr || '',
  }
}
function toServer(f) {
  return {
    name: 'Default',
    logoUrl: f.logoUrl || null,
    primaryColor: f.primaryColor || null,
    secondaryColor: f.secondaryColor || null,
    legalName: f.legalName || null,
    phone: f.phone || null,
    supportEmail: f.supportEmail || null,
    website: f.website || null,
    mailingAddress: empty(f.mailingAddress) ? null : f.mailingAddress,
    businessAddress: empty(f.businessAddress) ? null : f.businessAddress,
    social: empty(f.social) ? null : f.social,
    notes: f.notes || null,
    howItWorksEnabled: !!f.howItWorksEnabled,
    howItWorksEn: f.howItWorksEn?.trim() ? f.howItWorksEn : null,
    howItWorksFr: f.howItWorksFr?.trim() ? f.howItWorksFr : null,
  }
}
function empty(o) { return !o || Object.values(o).every(v => !v) }

// ─── Tax rates ──────────────────────────────────────────────────────────
function TaxRates() {
  const { t } = useLanguage()
  const [rows, setRows] = useState([])
  const [presets, setPresets] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState(null)
  const [presetCode, setPresetCode] = useState('')

  useEffect(() => {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/tax-rates')
      .then(r => {
        setRows((r.data?.taxRates || []).map(t => ({
          name:      t.Name,
          rate:      String(t.Rate),
          baseMode:  t.BaseMode,
          appliesTo: t.AppliesTo,
        })))
        setPresets(r.data?.presets || [])
      })
      .catch(e => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [])

  function update(i, k, v) { setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r)) }
  function remove(i) { setRows(rs => rs.filter((_, j) => j !== i)) }
  function addRow() { setRows(rs => [...rs, { name: '', rate: '', baseMode: 'subtotal', appliesTo: 'all' }]) }
  function applyPreset(code) {
    const p = presets.find(x => x.code === code)
    if (!p) return
    if (rows.length > 0 && !confirm(t('sp_confirm_replace_tax_lines', 'Replace your current {count} tax lines with the {preset} preset?', { count: rows.length, preset: p.label }))) return
    setRows((p.lines || []).map(l => ({
      name: l.name, rate: String(l.rate), baseMode: l.baseMode, appliesTo: 'all',
    })))
    setPresetCode('')
  }

  async function save() {
    setSaving(true); setError(null); setSavedAt(null)
    try {
      const taxRates = rows
        .filter(r => r.name.trim() && r.rate !== '')
        .map((r, i) => ({
          name:      r.name.trim(),
          rate:      Number(r.rate),
          baseMode:  r.baseMode,
          appliesTo: r.appliesTo,
          sortOrder: i,
        }))
      await tournamentApi.put('/portal/reseller/tax-rates', { taxRates })
      setSavedAt(new Date())
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    } finally { setSaving(false) }
  }

  // Live preview against $1000 subtotal
  const preview = computePreview(rows, 1000)

  if (loading) return <div style={{ color: '#64748b' }}>{t('sp_loading_tax_rates', 'Loading tax rates…')}</div>

  return (
    <div>
      {error && <div style={{ ...errorBox, marginBottom: 12 }}>{error}</div>}

      {/* Preset picker */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{t('sp_tax_quick_add', 'Quick add:')}</span>
        <select
          value={presetCode}
          onChange={e => { setPresetCode(e.target.value); applyPreset(e.target.value) }}
          style={{ ...inp, width: 280 }}
        >
          <option value="">{t('sp_tax_choose_jurisdiction', 'Choose a jurisdiction…')}</option>
          {presets.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
        </select>
      </div>

      {/* Rows */}
      {rows.length === 0 ? (
        <div style={{ padding: 16, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 6, color: '#64748b', fontSize: 13, textAlign: 'center' }}>
          {t('sp_tax_no_lines_prefix', 'No tax lines configured — pick a preset above or click')} <b>{t('sp_btn_add_tax_line', '+ Add tax line')}</b> {t('sp_tax_no_lines_suffix', 'below.')}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>{t('sp_name_label', 'Name')}</th>
                <th style={th}>{t('sp_tax_th_rate_pct', 'Rate %')}</th>
                <th style={th}>{t('sp_tax_th_calc_base', 'Calculation base')}</th>
                <th style={th}>{t('sp_tax_th_applies_to', 'Applies to')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={td}><input style={{ ...inp, width: 120 }} placeholder="GST" value={r.name} onChange={e => update(i, 'name', e.target.value)} /></td>
                  <td style={td}>
                    <input
                      type="number" step="0.001" min={0} max={100}
                      style={{ ...inp, width: 80 }}
                      placeholder="5"
                      value={r.rate === '' ? '' : (Number(r.rate) * 100).toString()}
                      onChange={e => update(i, 'rate', e.target.value === '' ? '' : (Number(e.target.value) / 100).toFixed(5))}
                    />
                  </td>
                  <td style={td}>
                    <select style={{ ...inp, width: 220 }} value={r.baseMode} onChange={e => update(i, 'baseMode', e.target.value)}>
                      <option value="subtotal">{t('sp_tax_base_subtotal_only', 'Subtotal only')}</option>
                      <option value="compound">{t('sp_tax_base_compound', 'Subtotal + previous compound taxes')}</option>
                    </select>
                  </td>
                  <td style={td}>
                    <select style={{ ...inp, width: 150 }} value={r.appliesTo} onChange={e => update(i, 'appliesTo', e.target.value)}>
                      <option value="all">{t('sp_tax_applies_everything', 'Everything')}</option>
                      <option value="hotel_only">{t('sp_tax_applies_hotel_only', 'Hotel only')}</option>
                      <option value="golf_only">{t('sp_tax_applies_golf_only', 'Golf only')}</option>
                      <option value="addons_only">{t('sp_tax_applies_addons_only', 'Add-ons only')}</option>
                    </select>
                  </td>
                  <td style={td}><button onClick={() => remove(i)} style={{ ...secondaryBtn, padding: '6px 12px', color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove', 'Remove')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <button onClick={addRow} style={secondaryBtn}>{t('sp_btn_add_tax_line', '+ Add tax line')}</button>
      </div>

      {/* Live preview */}
      {rows.length > 0 && (
        <div style={{ marginTop: 14, padding: 12, background: '#f0fdfa', border: '1px solid #a7f3d0', borderRadius: 6, fontSize: 13 }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: '#0f766e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            {t('sp_tax_preview_heading', 'Preview on a $1,000 sale')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{t('sp_subtotal', 'Subtotal')}</span><strong>$1,000.00</strong></div>
            {preview.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#475569' }}>
                <span>{l.name} ({(l.rate * 100).toFixed(3).replace(/\.?0+$/, '')}%{l.baseMode === 'compound' ? ', ' + t('sp_tax_compound_suffix', 'compound') : ''})</span>
                <span>+${l.amount.toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #86efac', paddingTop: 4, marginTop: 2, fontWeight: 700, color: '#0f766e' }}>
              <span>{t('sp_total', 'Total')}</span><span>${preview.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 14 }}>
        {savedAt && <span style={{ color: '#166534', fontSize: 12 }}>✓ {t('sp_saved_at', 'Saved at')} {savedAt.toLocaleTimeString()}</span>}
        <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
          {saving ? t('sp_saving', 'Saving…') : t('sp_btn_save_tax_rates', 'Save tax rates')}
        </button>
      </div>
    </div>
  )
}

function computePreview(rows, subtotal) {
  const valid = rows.filter(r => r.name.trim() && r.rate !== '' && Number(r.rate) > 0)
  const lines = []
  let runningCompoundBase = subtotal
  for (const r of valid) {
    const rate = Number(r.rate)
    const base = r.baseMode === 'compound' ? runningCompoundBase : subtotal
    const amount = Math.round(base * rate * 100) / 100
    lines.push({ name: r.name, rate, baseMode: r.baseMode, amount })
    if (r.baseMode === 'compound') runningCompoundBase += amount
  }
  const total = subtotal + lines.reduce((s, l) => s + l.amount, 0)
  return { lines, total }
}

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }
const td = { padding: '10px 12px', verticalAlign: 'middle' }

const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const row = { display: 'flex', gap: 12 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const successBox = { padding: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 6 }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, cursor: 'pointer' }
